import en from './locales/en.js';
import ar from './locales/ar.js';

export const LOCALES = ['en', 'ar'];
export const DEFAULT_LOCALE = 'en';
export const DIRECTION = { en: 'ltr', ar: 'rtl' };
export const CATALOGUES = { en, ar };

const PLURAL_CATEGORIES = new Set(['zero', 'one', 'two', 'few', 'many', 'other']);

function isPluralMessage(value) {
  return (
    value &&
    typeof value === 'object' &&
    Object.keys(value).length > 0 &&
    Object.keys(value).every((k) => PLURAL_CATEGORIES.has(k))
  );
}

/** Dotted key paths for every leaf message (a plural object is one leaf). */
export function flattenKeys(obj, prefix = '') {
  const out = [];
  for (const [key, value] of Object.entries(obj || {})) {
    const path = prefix ? `${prefix}.${key}` : key;
    if (value && typeof value === 'object' && !isPluralMessage(value)) {
      out.push(...flattenKeys(value, path));
    } else {
      out.push(path);
    }
  }
  return out.sort();
}

/** Keys present in `reference` but absent from `candidate`. */
export function missingKeys(reference, candidate) {
  const have = new Set(flattenKeys(candidate));
  return flattenKeys(reference).filter((k) => !have.has(k));
}

function lookup(catalogue, key) {
  return key.split('.').reduce((o, k) => (o == null ? undefined : o[k]), catalogue);
}

function interpolate(template, vars) {
  return String(template).replace(/\{(\w+)\}/g, (_, name) =>
    vars && vars[name] !== undefined ? String(vars[name]) : `{${name}}`
  );
}

/**
 * Translate `key` into `locale`. Plural messages select via Intl.PluralRules
 * on `vars.count`. A missing key returns the key itself — visible, greppable,
 * and never the string "undefined" in front of a user.
 */
export function t(locale, key, vars = {}) {
  const catalogue = CATALOGUES[locale] || CATALOGUES[DEFAULT_LOCALE];
  let message = lookup(catalogue, key);
  if (message === undefined) message = lookup(CATALOGUES[DEFAULT_LOCALE], key);
  if (message === undefined) return key;

  if (isPluralMessage(message)) {
    const count = Number(vars.count ?? 0);
    const category = new Intl.PluralRules(locale).select(count);
    message = message[category] ?? message.other ?? Object.values(message)[0];
    return interpolate(message, { ...vars, count });
  }
  return interpolate(message, vars);
}

export default { LOCALES, DEFAULT_LOCALE, DIRECTION, CATALOGUES, flattenKeys, missingKeys, t };
